<script>
import { http } from 'App/http';
import ClientForm from '@/online_booking/new/components/common_booking/ClientForm.vue'
import Card from '@/online_booking/new/components/common/Card';
import StepCounter from '@/online_booking/new/components/common/StepCounter';
import { mapActions, mapGetters, mapState } from 'vuex';
import PreviousButton from '@/online_booking/new/components/common/PreviousButton';
import EpButton from '@/system/components/global/button';
import { bookingKeys } from '@/online_booking/new/helpers/constants';

export default {
    name: 'FillInformationGiftcard',
    components: {
        PreviousButton,
        Card,
        ClientForm,
        StepCounter,
        EpButton,
    },
    data() {
        return {
            active: false,
            active_done: false,
            permalink: null,
            user: {},
            logged_in_client: {},
            consent_settings: null,
            consent_accepted: false,
            is_discount_codes_active: false,
            discount_code_code: '',
            giftcard: {},
            form: {
                name: '',
                email: '',
                address : '',
                address_line_2: '',
                zip : '',
                city : '',
                zone: '',
                country: '',
            },
            postingData: false,
            hasLoadedData: false
        }
    },

    mounted() {
        this.$event_bus.$on('LoadData', (data) => {
            this.permalink = data.permalink;
            this.user = data.user;
            this.form.country = this.user.country
            this.consent_settings = data.consent_settings;
            this.logged_in_client = data.logged_in_client;
            this.hasLoadedData = true
            this.is_discount_codes_active = data.is_discount_codes_active;

            const loggedInText = data.logged_in_client ? data.logged_in_client.name : '';
            this.setSelectedValue({
                key: bookingKeys.FILL_INFORMATION,
                value: data.logged_in_client ? loggedInText : ''
            });
        });

        this.$event_bus.$on('showGiftcardFillInformation', (giftcard, data) => {
            this.active = true;
            this.giftcard = giftcard;
            this.setCurrentStep('fillInformation');
        });

        this.$event_bus.$on('GiftcardsShowed', () => {
            this.active = false;
            this.active_done = false;
        });
    },

    methods: {
        ...mapActions('OnlineBooking', ['setCurrentStep', 'setSelectedValue']),
        titleClicked() {
            this.active_done = false;
            this.$event_bus.$emit('GiftcardFillInformationShowed');
        },

        getFormData () {
            const data = {
                ...this.form,
                giftcard_name: this.giftcard.name,
                amount: this.giftcard.amount
            }

            if (this.consent_accepted) {
                data.consent_accepted = true
            }

            if (this.discount_code_code != '') {
                data.discount_code = this.discount_code_code
            }

            return data
        },

        infoAdded() {
            this.errors.clear();

            this.postingData = true;

            http.post(`/giftcards/${this.permalink}/choose-client`, this.getFormData())
                .then(({ data }) => {
                    this.postingData = false;

                    if (data.success) {
                        this.active_done = true;
                    }

                    if (data.require_payment && data.payment_intent) {
                        this.$event_bus.$emit('ShowGiftcardPayment', this.giftcard, data);
                    }
                })
                .catch(({ error, errorBag }) => {
                    this.postingData = false;
                    this.$setErrorBag(errorBag);
                });
        }
    },

    watch: {
        logged_in_client(client) {
            if (client) {
                this.form.email = client.email;
                this.form.name = client.name;
            }
        },

        'form.name' (newValue) {
            // to make sure sidebar has the details of the client if they are NOT logged in
            this.setSelectedValue({
                key: bookingKeys.FILL_INFORMATION,
                value: newValue ? newValue : '',
            });
        },
    },

    computed: {
        ...mapGetters('OnlineBooking', ['currentStepNumber', 'totalStepCount', 'getPreviousStepId']),
        ...mapState('OnlineBooking', ['currentStep']),

        consent_required() {
            return this.consent_settings && this.consent_settings.is_active && this.consent_settings.required_vouchers
                && (!this.logged_in_client || this.logged_in_client && (!this.logged_in_client.consent || this.logged_in_client.consent.is_consent_outdated));
        },

        isFillInformationStepActive () {
            return this.currentStep === 'fillInformation';
        },
    }
}
</script>

<template>
    <div id="fill-information-panel" v-if="isFillInformationStepActive">
        <card class="p-0 rounded-lg">
            <h4 class="section-title">
                <step-counter :current="currentStepNumber" :total="totalStepCount" />
                {{ trans('online_booking.sidebar.your_details') }}
            </h4>
            <div
                id="collapseFillInfo"
                role="tabpanel"
                aria-labelledby="headingFillInfo"
                data-parent="#booking-accordion"
            >
                <div class="panel-body pb-0">
                    <div class="row">
                        <div class="col-xs-12">

                            <form id="giftcards-fill-info-frm" role="form" @submit.prevent="">

                                <!-- Alert - Already logged in -->
                                <ep-banner class="mb-6" v-if="logged_in_client">
                                    <i class="zmdi zmdi-info-outline m-r-10 alert-ico"></i>
                                    <span>{{ trans('online_booking.logged_in_as') }}</span>&nbsp;<strong>{{ logged_in_client.name }}</strong>
                                </ep-banner>
                                <!-- End of Alert -->

                                <client-form
                                    class="online-booking-client-form"
                                    v-if="hasLoadedData"
                                    v-model="form"
                                    :nameOptions="{ display: !logged_in_client, required: true, label: trans('giftcards.purchasing.your_name') }"
                                    :emailOptions="{ display: true, required: true, label: trans('giftcards.purchasing.your_email'), description: trans('giftcards.purchasing.your_email_desc') }"
                                    :phoneOptions="{ display: false, required: false }"
                                    :addressOptions="{ display: !logged_in_client, required: true }"
                                    :cprOptions="{ display: false, required: false }"
                                    :dobOptions="{ display: false, required: false }"
                                    :validationErrors="errors"
                                    show-required-indicator
                                >
                                </client-form>

                                <div
                                    class="form-group"
                                    :class="{ 'has-error' : errors.has('discount_code') }"
                                    v-if="is_discount_codes_active"
                                >
                                    <div class="input-group">
                                        <div class="input-group-addon">
                                            <i class="fa fa-tag m-r-5"></i>
                                            {{ trans('discount_codes.discount_code') }}
                                        </div>

                                        <input
                                            class="form-control"
                                            name="discount_code"
                                            type="text"
                                            v-model="discount_code_code"
                                            autocomplete="off"
                                        />
                                    </div>
                                    <span class="help-block m-b-0" v-if="errors.has('discount_code')">
                                    {{ errors.first('discount_code') }}
                                </span>
                                    <small class="text-muted" v-else>{{ trans('discount_codes.input_description') }}</small>

                                </div>

                                <div class="form-group" :class="{ 'has-error' : errors.has('consent_accepted') }" v-if="consent_required">
                                    <div class="checkbox">
                                        <input id="consent_accepted" type="checkbox" name="consent_accepted" v-model.boolean="consent_accepted">
                                        <label for="consent_accepted">{{ consent_settings.checkbox_label }}</label>
                                        <a href="" data-toggle="modal" data-target="#consentModal" >({{ trans('online_booking.read_more') }})</a>
                                        <span v-show="errors.has('consent_accepted')" class="help-block m-b-0">{{ errors.first('consent_accepted') }}</span>

                                    </div>
                                </div>
                            </form>

                        </div>
                    </div>
                </div>
            </div>
            <div class="flex justify-between flex-wrap gap-6 p-6 pt-0">
                <previous-button :previous-step-id="getPreviousStepId" />
                <ep-button
                    :loading="postingData"
                    :disabled="postingData"
                    @click="infoAdded"
                    type="submit"
                >
                    <span class="hidden sm:inline">{{ trans('vouchers.confirm_and_pay') }}</span>
                    <span class="sm:hidden">{{ trans('global.continue') }}</span>
                    <i class="zmdi zmdi-arrow-right ml-3"></i>
                </ep-button>
            </div>
        </card>
    </div>
</template>
